import type { SortDirection, TableColumn } from "./types";

/**
 * Whether a sort value counts as "empty" — nullish, blank, or a placeholder
 * dash ("-"/"—"). Empty values always sort to the bottom, in BOTH directions.
 *
 * @param value - A column sort value.
 * @returns True when the value should be treated as empty.
 */
function isEmptySortValue(
  value: string | number | null | undefined,
): boolean {
  if (value == null) return true;
  const text = String(value).trim();
  return text === "" || text === "-" || text === "—";
}

/**
 * Compare two NON-empty column values with type-aware ordering (numbers
 * numerically, strings via locale compare). Emptiness is handled by the caller
 * so it can stay direction-independent.
 *
 * @param a - First value.
 * @param b - Second value.
 * @returns Negative if a<b, positive if a>b, 0 if equal (ascending order).
 */
function compareValues(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

/**
 * Produce a sorted copy of `rows`. A stable primary comparator (if provided) is
 * applied first; the user-selected column acts as the tiebreaker. JS `Array.sort`
 * is stable in all supported browsers, so equal elements keep their order.
 *
 * @typeParam T - The row data type.
 * @param rows - Rows to sort (not mutated).
 * @param columns - Column definitions (used to resolve the sort accessor).
 * @param sortKey - Key of the column to sort by, or null for no column sort.
 * @param direction - Sort direction for the column sort.
 * @param primaryComparator - Optional stable pre-sort comparator.
 * @returns A new, sorted array.
 */
export function sortRows<T>(
  rows: T[],
  columns: TableColumn<T>[],
  sortKey: string | null,
  direction: SortDirection,
  primaryComparator?: (a: T, b: T) => number,
): T[] {
  const column = sortKey ? columns.find((c) => c.key === sortKey) : undefined;
  const accessor = column?.sortAccessor;

  return [...rows].sort((a, b) => {
    if (primaryComparator) {
      const primary = primaryComparator(a, b);
      if (primary !== 0) return primary;
    }
    if (!accessor) return 0;
    const av = accessor(a);
    const bv = accessor(b);
    // Empty / placeholder rows always sink to the bottom, regardless of the
    // asc/desc direction (so "—" / "None scheduled" never lead the list).
    const aEmpty = isEmptySortValue(av);
    const bEmpty = isEmptySortValue(bv);
    if (aEmpty || bEmpty) {
      if (aEmpty && bEmpty) return 0;
      return aEmpty ? 1 : -1;
    }
    const comparison = compareValues(av, bv);
    return direction === "asc" ? comparison : -comparison;
  });
}
